import React, { useMemo, useState, cloneElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Search, Download, Eye, Ban, UserCheck, MessageSquare, AlertTriangle, CheckCircle2, XCircle, TrendingUp, MoreVertical, Plus, Clock, Calendar, MessageCircle, DollarSign, ChevronLeft, Mail, Phone, Globe, MapPin, Activity, Video, ShoppingBag, AlertOctagon, Send, Lock, Unlock, Crown, Gem, ShieldCheck as ShieldCheckIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
interface AdminAstrologersManagerProps {
  astrologers: any[];
  users: any[];
  orders: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  setCreatePanel: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminAstrologersManager({
  astrologers,
  users,
  orders,
  setActiveTab,
  setDetailPanel,
  setCreatePanel,
  success
}: AdminAstrologersManagerProps) {
  // --- STATE ---
  const [selectedAstrologer, setSelectedAstrologer] = useState<any | null>(null);
  const [profileTab, setProfileTab] = useState('main');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    verified: 'all',
    spec: 'all',
    complaints: 'all',
    risk: 'all',
    sort: 'newest'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // --- TAB-SPECIFIC STATE ---
  const [svcSearch, setSvcSearch] = useState('');
  const [svcPage, setSvcPage] = useState(1);
  const [ordSearch, setOrdSearch] = useState('');
  const [ordPage, setOrdPage] = useState(1);
  const [ordStatus, setOrdStatus] = useState('all');
  const [selectedChatId, setSelectedChatId] = useState(1);
  const [conPage, setConPage] = useState(1);
  const [conFormat, setConFormat] = useState('all');
  const [compPage, setCompPage] = useState(1);
  const [compStatus, setCompStatus] = useState('all');
  const [finPage, setFinPage] = useState(1);
  const [revPage, setRevPage] = useState(1);
  const [revRating, setRevRating] = useState('all');
  // --- DATA ENRICHMENT (Mocking missing marketplace data) ---
  const enrichedAstrologers = useMemo(() => {
    return astrologers.map((a, index) => {
      const isHighRisk = index % 7 === 0; // Mock high risk
      const hasComplaints = index % 5 === 0;
      const isOnline = index % 3 === 0;
      return {
        ...a,
        lastLogin: isOnline ? 'Онлайн' : `Вчера, ${10 + index % 12}:30`,
        regDate: `1${index % 9 + 1}.0${index % 8 + 1}.2023`,
        activeConsultations: index % 4,
        complaintsCount: hasComplaints ? index % 3 + 1 : 0,
        hasActiveChats: index % 2 === 0,
        isOnline,
        riskScore: isHighRisk ? 'high' : hasComplaints ? 'medium' : 'low',
        riskReasons: isHighRisk ? ['Попытка увода клиента (WhatsApp)', 'Обмен контактами'] : [],
        languages: ['Русский', index % 2 === 0 ? 'Английский' : 'Испанский'],
        country: index % 3 === 0 ? 'Казахстан' : 'Россия',
        phone: '+7 (999) ' + Math.floor(1000000 + Math.random() * 9000000),
        avgResponseTime: `${5 + index % 15} мин`,
        totalEarned: parseInt(a.earnings?.replace(/\D/g, '') || '0') * 10,
        badge: index % 5 === 0 ? 'TOP' : index % 3 === 0 ? 'PRO' : index % 7 === 0 ? 'Featured' : null
      };
    });
  }, [astrologers]);
  // --- KPIs ---
  const totalCount = enrichedAstrologers.length;
  const activeCount = enrichedAstrologers.filter(a => a.status === 'active').length;
  const pendingCount = enrichedAstrologers.filter(a => a.status === 'pending').length;
  const complaintsCount = enrichedAstrologers.filter(a => a.complaintsCount > 0).length;
  const onlineCount = enrichedAstrologers.filter(a => a.isOnline).length;
  const blockedCount = enrichedAstrologers.filter(a => a.status === 'blocked').length;
  const zeroOrdersCount = enrichedAstrologers.filter(a => a.orders === 0).length;
  const avgRating = (enrichedAstrologers.reduce((acc, a) => acc + (a.rating || 5), 0) / totalCount).toFixed(1);
  // --- HANDLERS ---
  const handleExport = () => {
    success(`Экспорт ${filteredAstrologers.length} астрологов начат`);
  };
  const handleMassAction = (action: string) => {
    if (selectedIds.size === 0) return;
    success(`Действие "${action}" применено к ${selectedIds.size} астрологам`);
    setSelectedIds(new Set());
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === currentAstrologers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentAstrologers.map(a => a.id)));
    }
  };
  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  // --- FILTERING & PAGINATION ---
  const filteredAstrologers = useMemo(() => {
    return enrichedAstrologers.filter(a => {
      const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) || a.email.toLowerCase().includes(searchQuery.toLowerCase()) || a.spec.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      if (filters.status !== 'all' && a.status !== filters.status) return false;
      if (filters.spec !== 'all' && !a.spec.toLowerCase().includes(filters.spec.toLowerCase())) return false;
      if (filters.verified !== 'all') {
        if (filters.verified === 'true' && !a.verified) return false;
        if (filters.verified === 'false' && a.verified) return false;
      }
      if (filters.complaints === 'yes' && a.complaintsCount === 0) return false;
      if (filters.complaints === 'no' && a.complaintsCount > 0) return false;
      if (filters.risk !== 'all' && a.riskScore !== filters.risk) return false;
      return true;
    }).sort((a, b) => {
      if (filters.sort === 'income_desc') return b.totalEarned - a.totalEarned;
      if (filters.sort === 'rating_desc') return b.rating - a.rating;
      if (filters.sort === 'orders_desc') return b.orders - a.orders;
      return 0; // newest default
    });
  }, [enrichedAstrologers, searchQuery, filters]);
  const totalPages = Math.ceil(filteredAstrologers.length / itemsPerPage);
  const currentAstrologers = filteredAstrologers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // --- RENDERERS ---
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string, onClickFilter?: Partial<typeof filters>) => <Card padding="sm" hoverable className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group transition-all hover:border-indigo-300 hover:shadow-md" onClick={() => {
    if (onClickFilter) {
      setFilters({
        ...filters,
        ...onClickFilter
      });
      setCurrentPage(1);
    }
  }} data-id="element-1927">
      <div className="flex justify-between items-start" data-id="element-1928">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-1929">
          {icon}
        </div>
        {onClickFilter && <div className="text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1930">
            Фильтр ↗
          </div>}
      </div>
      <div data-id="element-1931">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-1932">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-1933">{value}</div>
      </div>
    </Card>;
  // ==========================================
  // DETAILED PROFILE VIEW
  // ==========================================
  if (selectedAstrologer) {
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-1934">
        {/* Profile Header */}
        <div className="flex items-center justify-between" data-id="element-1935">
          <div className="flex items-center gap-4" data-id="element-1936">
            <Button variant="ghost" icon={<ChevronLeft className="w-4 h-4" data-id="element-1938" />} onClick={() => setSelectedAstrologer(null)} data-id="element-1937">
              Назад к списку
            </Button>
            <div className="h-6 w-px bg-slate-200" data-id="element-1939" />
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2" data-id="element-1940">
              Профиль астролога
              {selectedAstrologer.riskScore === 'high' && <Badge variant="error" size="sm" className="ml-2 animate-pulse" data-id="element-1941">
                  <AlertTriangle className="w-3 h-3 mr-1" data-id="element-1942" /> High Risk
                </Badge>}
            </h2>
          </div>
          <div className="flex items-center gap-2" data-id="element-1943">
            <Button variant="outline" className={selectedAstrologer.status === 'active' ? 'text-rose-600 hover:bg-rose-50' : 'text-emerald-600 hover:bg-emerald-50'} icon={selectedAstrologer.status === 'active' ? <Lock className="w-4 h-4" data-id="element-1945" /> : <Unlock className="w-4 h-4" data-id="element-1946" />} onClick={() => success(`Статус изменен`)} data-id="element-1944">
              {selectedAstrologer.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
            </Button>
            <Button variant="primary" icon={<MessageSquare className="w-4 h-4" data-id="element-1948" />} onClick={() => {
            setSelectedAstrologer(null);
            setActiveTab('full_chat');
          }} data-id="element-1947">
              Написать
            </Button>
          </div>
        </div>

        {/* Profile Summary Card */}
        <Card padding="md" className="border-slate-200 shadow-sm relative overflow-hidden" data-id="element-1949">
          {selectedAstrologer.riskScore === 'high' && <div className="absolute top-0 left-0 w-full h-1 bg-rose-500" data-id="element-1950" />}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center" data-id="element-1951">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xl flex-shrink-0 border-2 border-white shadow-md" data-id="element-1952">
              {selectedAstrologer.name.split(' ').map((n: string) => n[0]).join('')}
            </div>
            <div className="flex-1" data-id="element-1953">
              <div className="flex items-center gap-2 mb-1" data-id="element-1954">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-1955">
                  {selectedAstrologer.name}
                </h3>
                {selectedAstrologer.badge === 'TOP' && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200 cursor-help" title="TOP — лучший рейтинг и отзывы" data-id="element-1956">
                    <Crown className="w-3 h-3" data-id="element-1957" /> TOP
                  </span>}
                {selectedAstrologer.badge === 'PRO' && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200 cursor-help" title="PRO — подтверждённый профессионал" data-id="element-1958">
                    <ShieldCheckIcon className="w-3 h-3" data-id="element-1959" /> PRO
                  </span>}
                {selectedAstrologer.badge === 'Featured' && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200 cursor-help" title="Рекомендуем — выбор редакции" data-id="element-1960">
                    <Gem className="w-3 h-3" data-id="element-1961" /> Рек.
                  </span>}
                {selectedAstrologer.verified && <Badge variant="success" size="sm" data-id="element-1962">
                    <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-1963" /> Верифицирован
                  </Badge>}
                {selectedAstrologer.isOnline && <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full" data-id="element-1964">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1965" />{' '}
                    Онлайн
                  </span>}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3" data-id="element-1966">
                <span className="flex items-center gap-1" data-id="element-1967">
                  <Mail className="w-3.5 h-3.5 text-slate-400" data-id="element-1968" />{' '}
                  {selectedAstrologer.email}
                </span>
                <span className="flex items-center gap-1" data-id="element-1969">
                  <Phone className="w-3.5 h-3.5 text-slate-400" data-id="element-1970" />{' '}
                  {selectedAstrologer.phone}
                </span>
                <span className="flex items-center gap-1" data-id="element-1971">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" data-id="element-1972" />{' '}
                  {selectedAstrologer.country}
                </span>
                <span className="flex items-center gap-1" data-id="element-1973">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" data-id="element-1974" /> На
                  платформе с {selectedAstrologer.regDate}
                </span>
              </div>
              <div className="flex gap-5" data-id="element-1975">
                <div data-id="element-1976">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider" data-id="element-1977">
                    Рейтинг
                  </div>
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1" data-id="element-1978">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-1979" />{' '}
                    {selectedAstrologer.rating}
                  </div>
                </div>
                <div data-id="element-1980">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider" data-id="element-1981">
                    Заказы
                  </div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-1982">
                    {selectedAstrologer.orders}
                  </div>
                </div>
                <div data-id="element-1983">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider" data-id="element-1984">
                    Заработано
                  </div>
                  <div className="text-sm font-semibold text-emerald-600" data-id="element-1985">
                    {selectedAstrologer.earnings}
                  </div>
                </div>
                <div data-id="element-1986">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider" data-id="element-1987">
                    Жалобы
                  </div>
                  <div className={`text-sm font-semibold ${selectedAstrologer.complaintsCount > 0 ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-1988">
                    {selectedAstrologer.complaintsCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Profile Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide border-b border-slate-200" data-id="element-1989">
          {[{
          id: 'main',
          label: 'Основное',
          icon: <UserCheck className="w-4 h-4" data-id="element-1990" />
        }, {
          id: 'services',
          label: 'Услуги',
          icon: <ShoppingBag className="w-4 h-4" data-id="element-1991" />
        }, {
          id: 'orders',
          label: 'Заказы',
          icon: <Clock className="w-4 h-4" data-id="element-1992" />
        }, {
          id: 'consultations',
          label: 'Консультации',
          icon: <Video className="w-4 h-4" data-id="element-1993" />
        }, {
          id: 'chats',
          label: 'Чаты (Мониторинг)',
          icon: <MessageCircle className="w-4 h-4" data-id="element-1994" />,
          alert: selectedAstrologer.riskScore === 'high'
        }, {
          id: 'complaints',
          label: 'Жалобы',
          icon: <AlertTriangle className="w-4 h-4" data-id="element-1995" />,
          count: selectedAstrologer.complaintsCount
        }, {
          id: 'finance',
          label: 'Доход и Выплаты',
          icon: <DollarSign className="w-4 h-4" data-id="element-1996" />
        }, {
          id: 'reviews',
          label: 'Отзывы',
          icon: <Star className="w-4 h-4" data-id="element-1997" />
        }, {
          id: 'risk',
          label: 'Risk-контроль',
          icon: <AlertTriangle className="w-4 h-4" data-id="element-1998" />,
          alert: selectedAstrologer.riskScore === 'high'
        }].map(tab => <button key={tab.id} onClick={() => setProfileTab(tab.id)} className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${profileTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-1999">
              {cloneElement(tab.icon as React.ReactElement, {
            className: 'w-3.5 h-3.5'
          })}
              {tab.label}
              {tab.count > 0 && <span className="bg-rose-100 text-rose-600 py-0.5 px-2 rounded-full text-[10px] font-bold" data-id="element-2000">
                  {tab.count}
                </span>}
              {tab.alert && <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" data-id="element-2001" />}
            </button>)}
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]" data-id="element-2002">
          {profileTab === 'main' && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2003">
              <Card padding="sm" data-id="element-2004">
                <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-2005">
                  Информация о специалисте
                </h4>
                <div className="space-y-3" data-id="element-2006">
                  <div data-id="element-2007">
                    <div className="text-[10px] text-slate-400 mb-0.5" data-id="element-2008">
                      Специализация
                    </div>
                    <div className="text-sm font-medium text-slate-900" data-id="element-2009">
                      {selectedAstrologer.spec}
                    </div>
                  </div>
                  <div data-id="element-2010">
                    <div className="text-[10px] text-slate-400 mb-0.5" data-id="element-2011">
                      Языки консультаций
                    </div>
                    <div className="flex gap-2" data-id="element-2012">
                      {selectedAstrologer.languages.map((l: string) => <Badge key={l} variant="neutral" data-id="element-2013">
                          {l}
                        </Badge>)}
                    </div>
                  </div>
                  <div data-id="element-2014">
                    <div className="text-[10px] text-slate-400 mb-0.5" data-id="element-2015">
                      О себе
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed" data-id="element-2016">
                      Профессиональный астролог с опытом более 5 лет. Помогаю
                      разобраться в предназначении, отношениях и финансах с
                      помощью натальной карты и прогностики.
                    </p>
                  </div>
                </div>
              </Card>
              <Card padding="sm" data-id="element-2017">
                <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-2018">
                  Метрики платформы
                </h4>
                <div className="space-y-2" data-id="element-2019">
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100" data-id="element-2020">
                    <span className="text-xs text-slate-600" data-id="element-2021">
                      Среднее время ответа
                    </span>
                    <span className="text-xs font-semibold text-slate-900" data-id="element-2022">
                      {selectedAstrologer.avgResponseTime}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100" data-id="element-2023">
                    <span className="text-xs text-slate-600" data-id="element-2024">
                      Конверсия в заказ
                    </span>
                    <span className="text-xs font-semibold text-emerald-600" data-id="element-2025">
                      12.4%
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-100" data-id="element-2026">
                    <span className="text-xs text-slate-600" data-id="element-2027">
                      Возвращаемость клиентов
                    </span>
                    <span className="text-xs font-semibold text-indigo-600" data-id="element-2028">
                      34%
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5" data-id="element-2029">
                    <span className="text-xs text-slate-600" data-id="element-2030">
                      Уровень риска (Risk Score)
                    </span>
                    <Badge variant={selectedAstrologer.riskScore === 'high' ? 'error' : selectedAstrologer.riskScore === 'medium' ? 'warning' : 'success'} data-id="element-2031">
                      {selectedAstrologer.riskScore.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </Card>
            </div>}

          {profileTab === 'chats' && <div className="space-y-4" data-id="element-2032">
              {selectedAstrologer.riskScore === 'high' && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-id="element-2033">
                  <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" data-id="element-2034" />
                  <div data-id="element-2035">
                    <h4 className="text-sm font-bold text-rose-900" data-id="element-2036">
                      Обнаружено подозрительное поведение
                    </h4>
                    <p className="text-xs text-rose-700 mt-1" data-id="element-2037">
                      Система авто-модерации зафиксировала попытки обмена
                      контактами или увода клиента с платформы. Причины:{' '}
                      {selectedAstrologer.riskReasons.join(', ')}
                    </p>
                  </div>
                </div>}

              <Card padding="none" className="overflow-hidden flex h-[500px]" data-id="element-2038">
                <div className="w-1/3 border-r border-slate-200 bg-slate-50 overflow-y-auto" data-id="element-2039">
                  <div className="p-3 border-b border-slate-200 font-bold text-sm text-slate-900" data-id="element-2040">
                    Активные диалоги
                  </div>
                  {[{
                id: 1,
                preview: 'Напишите мне в whatsapp...',
                risk: true
              }, {
                id: 2,
                preview: 'Спасибо за консультацию!',
                risk: false
              }, {
                id: 3,
                preview: 'Когда будет готов отчёт?',
                risk: false
              }].map(chat => <div key={chat.id} onClick={() => setSelectedChatId(chat.id)} className={`p-3 border-b border-slate-200 cursor-pointer transition-colors ${selectedChatId === chat.id ? 'bg-white border-l-2 border-l-indigo-500' : 'hover:bg-slate-100'}`} data-id="element-2041">
                      <div className="flex justify-between items-start mb-1" data-id="element-2042">
                        <div className="font-medium text-sm text-slate-900" data-id="element-2043">
                          Клиент #{8400 + chat.id}
                        </div>
                        <div className="text-[10px] text-slate-500" data-id="element-2044">14:30</div>
                      </div>
                      <div className="text-xs text-slate-600 truncate" data-id="element-2045">
                        {chat.preview}
                      </div>
                      {chat.risk && <Badge variant="error" size="sm" className="mt-2 text-[9px] px-1 py-0" data-id="element-2046">
                          Risk Flag
                        </Badge>}
                    </div>)}
                </div>
                <div className="flex-1 flex flex-col bg-white" data-id="element-2047">
                  <div className="p-4 border-b border-slate-200 flex justify-between items-center" data-id="element-2048">
                    <div className="font-bold text-slate-900" data-id="element-2049">
                      Чат с Клиентом #{8400 + selectedChatId}
                    </div>
                    <Button variant="outline" size="sm" icon={<Ban className="w-3 h-3" data-id="element-2051" />} onClick={() => success('Чат заблокирован')} data-id="element-2050">
                      Заблокировать чат
                    </Button>
                  </div>
                  <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-slate-50/50" data-id="element-2052">
                    {selectedChatId === 1 && <>
                        <div className="flex gap-3 max-w-[80%]" data-id="element-2053">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0" data-id="element-2054" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2055">
                            Здравствуйте! Я бы хотела заказать подробный разбор
                            совместимости.
                          </div>
                        </div>
                        <div className="flex gap-3 max-w-[80%] ml-auto flex-row-reverse" data-id="element-2056">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex-shrink-0" data-id="element-2057" />
                          <div className="bg-rose-50 border border-rose-200 rounded-2xl rounded-tr-none p-3 text-sm text-slate-900 shadow-sm relative" data-id="element-2058">
                            <div className="absolute -top-2 -right-2 bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-2059">
                              FLAGGED
                            </div>
                            Добрый день! Да, конечно. Но здесь неудобно
                            отправлять файлы. Напишите мне в{' '}
                            <span className="bg-rose-200 font-bold px-1 rounded" data-id="element-2060">
                              whatsapp
                            </span>{' '}
                            по номеру +79991234567, там обсудим детали.
                          </div>
                        </div>
                      </>}
                    {selectedChatId === 2 && <>
                        <div className="flex gap-3 max-w-[80%]" data-id="element-2061">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0" data-id="element-2062" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2063">
                            Здравствуйте! Хочу уточнить по консультации — можно
                            ли перенести на завтра?
                          </div>
                        </div>
                        <div className="flex gap-3 max-w-[80%] ml-auto flex-row-reverse" data-id="element-2064">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex-shrink-0" data-id="element-2065" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tr-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2066">
                            Конечно! Давайте перенесём на 15:00 завтра. Вам
                            удобно?
                          </div>
                        </div>
                        <div className="flex gap-3 max-w-[80%]" data-id="element-2067">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0" data-id="element-2068" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2069">
                            Да, отлично! Спасибо за консультацию!
                          </div>
                        </div>
                      </>}
                    {selectedChatId === 3 && <>
                        <div className="flex gap-3 max-w-[80%]" data-id="element-2070">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0" data-id="element-2071" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2072">
                            Добрый день! Когда будет готов мой отчёт по
                            натальной карте?
                          </div>
                        </div>
                        <div className="flex gap-3 max-w-[80%] ml-auto flex-row-reverse" data-id="element-2073">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex-shrink-0" data-id="element-2074" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tr-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2075">
                            Здравствуйте! Отчёт будет готов завтра к 18:00.
                            Пришлю вам уведомление.
                          </div>
                        </div>
                        <div className="flex gap-3 max-w-[80%]" data-id="element-2076">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex-shrink-0" data-id="element-2077" />
                          <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none p-3 text-sm text-slate-700 shadow-sm" data-id="element-2078">
                            Отлично, жду! Спасибо 🙏
                          </div>
                        </div>
                      </>}
                  </div>
                  <div className="p-3 border-t border-slate-200 bg-white" data-id="element-2079">
                    <div className="text-xs text-slate-500 text-center" data-id="element-2080">
                      Режим наблюдения. Вы не можете писать в этот чат.
                    </div>
                  </div>
                </div>
              </Card>
            </div>}

          {profileTab === 'services' && (() => {
          const mockServices = [{
            name: 'Натальная карта (Полный разбор)',
            type: 'Отчёт',
            price: '4 500 ₽',
            orders: 45,
            rating: 4.9,
            status: 'active'
          }, {
            name: 'Соляр (Прогноз на год)',
            type: 'Отчёт',
            price: '3 800 ₽',
            orders: 28,
            rating: 4.8,
            status: 'active'
          }, {
            name: 'Совместимость (Синастрия)',
            type: 'Отчёт',
            price: '5 000 ₽',
            orders: 12,
            rating: 5.0,
            status: 'active'
          }, {
            name: 'Консультация (60 мин)',
            type: 'Видео',
            price: '7 000 ₽',
            orders: 34,
            rating: 4.7,
            status: 'active'
          }, {
            name: 'Экспресс-ответ (Аудио)',
            type: 'Аудио',
            price: '1 500 ₽',
            orders: 89,
            rating: 4.5,
            status: 'paused'
          }, {
            name: 'Транзитный прогноз (месяц)',
            type: 'Отчёт',
            price: '2 000 ₽',
            orders: 12,
            rating: 4.6,
            status: 'paused'
          }, {
            name: 'Детский гороскоп',
            type: 'Отчёт',
            price: '3 500 ₽',
            orders: 56,
            rating: 4.9,
            status: 'active'
          }, {
            name: 'Финансовый гороскоп',
            type: 'Отчёт',
            price: '4 000 ₽',
            orders: 23,
            rating: 4.8,
            status: 'active'
          }, {
            name: 'Кармическое предназначение',
            type: 'Отчёт',
            price: '5 500 ₽',
            orders: 18,
            rating: 5.0,
            status: 'active'
          }, {
            name: 'Консультация (30 мин)',
            type: 'Видео',
            price: '4 000 ₽',
            orders: 41,
            rating: 4.7,
            status: 'active'
          }, {
            name: 'Разбор сложной ситуации',
            type: 'Текст',
            price: '2 500 ₽',
            orders: 67,
            rating: 4.4,
            status: 'active'
          }, {
            name: 'Подбор благоприятной даты',
            type: 'Услуга',
            price: '1 800 ₽',
            orders: 31,
            rating: 4.8,
            status: 'active'
          }];
          const filtered = mockServices.filter(s => s.name.toLowerCase().includes(svcSearch.toLowerCase()));
          const totalPages = Math.ceil(filtered.length / 5);
          const paginated = filtered.slice((svcPage - 1) * 5, svcPage * 5);
          return <div className="space-y-4" data-id="element-2081">
                  {/* Stats & Filters */}
                  <div className="flex flex-col sm:flex-row justify-between gap-4" data-id="element-2082">
                    <div className="flex flex-wrap gap-4" data-id="element-2083">
                      <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200" data-id="element-2084">
                        <div className="text-xs text-slate-500" data-id="element-2085">
                          Всего услуг
                        </div>
                        <div className="font-bold text-slate-900" data-id="element-2086">12</div>
                      </div>
                      <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-100" data-id="element-2087">
                        <div className="text-xs text-emerald-600" data-id="element-2088">Активных</div>
                        <div className="font-bold text-emerald-700" data-id="element-2089">10</div>
                      </div>
                      <div className="bg-amber-50 px-4 py-2 rounded-lg border border-amber-100" data-id="element-2090">
                        <div className="text-xs text-amber-600" data-id="element-2091">
                          Приостановлено
                        </div>
                        <div className="font-bold text-amber-700" data-id="element-2092">2</div>
                      </div>
                      <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200" data-id="element-2093">
                        <div className="text-xs text-slate-500" data-id="element-2094">
                          Средняя цена
                        </div>
                        <div className="font-bold text-slate-900" data-id="element-2095">3 758 ₽</div>
                      </div>
                    </div>
                    <div className="relative w-full sm:w-64" data-id="element-2096">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2097" />
                      <input type="text" placeholder="Поиск услуг..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" value={svcSearch} onChange={e => {
                  setSvcSearch(e.target.value);
                  setSvcPage(1);
                }} data-id="element-2098" />
                    </div>
                  </div>

                  <Card padding="none" className="overflow-hidden" data-id="element-2099">
                    <table className="w-full text-left" data-id="element-2100">
                      <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-2101">
                        <tr data-id="element-2102">
                          <th className="px-4 py-3 font-medium" data-id="element-2103">Услуга</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2104">Тип</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2105">Цена</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2106">Заказов</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2107">Рейтинг</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2108">Статус</th>
                          <th className="px-4 py-3 text-right font-medium" data-id="element-2109">
                            Действия
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-2110">
                        {paginated.map((service, idx) => <tr key={idx} className="hover:bg-slate-50" data-id="element-2111">
                            <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2112">
                              {service.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500" data-id="element-2113">
                              {service.type}
                            </td>
                            <td className="px-4 py-3 font-bold text-slate-900" data-id="element-2114">
                              {service.price}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600" data-id="element-2115">
                              {service.orders}
                            </td>
                            <td className="px-4 py-3" data-id="element-2116">
                              <div className="flex items-center gap-1 text-sm font-medium text-slate-900" data-id="element-2117">
                                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-2118" />
                                {service.rating}
                              </div>
                            </td>
                            <td className="px-4 py-3" data-id="element-2119">
                              <Badge variant={service.status === 'active' ? 'success' : 'warning'} size="sm" data-id="element-2120">
                                {service.status === 'active' ? 'Активна' : 'Приостановлена'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-2121">
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-indigo-600" onClick={() => success(`Редактирование услуги «${service.name}»`)} data-id="element-2122">
                                Изменить
                              </Button>
                            </td>
                          </tr>)}
                        {paginated.length === 0 && <tr data-id="element-2123">
                            <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-2124">
                              Ничего не найдено
                            </td>
                          </tr>}
                      </tbody>
                    </table>
                    {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center bg-slate-50/50" data-id="element-2125">
                        <Pagination currentPage={svcPage} totalPages={totalPages} onPageChange={setSvcPage} data-id="element-2126" />
                      </div>}
                  </Card>
                </div>;
        })()}

          {profileTab === 'orders' && (() => {
          const mockOrders = [{
            id: 'ORD-8401',
            client: 'Анна Ковалёва',
            service: 'Натальная карта',
            amount: '4 500 ₽',
            status: 'done',
            date: '15.03.2026'
          }, {
            id: 'ORD-8392',
            client: 'Дмитрий Соколов',
            service: 'Соляр',
            amount: '3 800 ₽',
            status: 'done',
            date: '14.03.2026'
          }, {
            id: 'ORD-8380',
            client: 'Елена Морозова',
            service: 'Совместимость',
            amount: '5 000 ₽',
            status: 'progress',
            date: '13.03.2026'
          }, {
            id: 'ORD-8371',
            client: 'Ольга Петрова',
            service: 'Консультация 60 мин',
            amount: '7 000 ₽',
            status: 'done',
            date: '12.03.2026'
          }, {
            id: 'ORD-8365',
            client: 'Игорь Волков',
            service: 'Натальная карта',
            amount: '4 500 ₽',
            status: 'done',
            date: '11.03.2026'
          }, {
            id: 'ORD-8350',
            client: 'Мария Лебедева',
            service: 'Транзитный прогноз',
            amount: '2 800 ₽',
            status: 'cancelled',
            date: '10.03.2026'
          }, {
            id: 'ORD-8342',
            client: 'Сергей Новиков',
            service: 'Консультация 30 мин',
            amount: '3 500 ₽',
            status: 'done',
            date: '09.03.2026'
          }, {
            id: 'ORD-8330',
            client: 'Татьяна Романова',
            service: 'Соляр',
            amount: '3 800 ₽',
            status: 'done',
            date: '08.03.2026'
          }, {
            id: 'ORD-8321',
            client: 'Алексей Кузнецов',
            service: 'Совместимость',
            amount: '5 000 ₽',
            status: 'done',
            date: '07.03.2026'
          }, {
            id: 'ORD-8315',
            client: 'Наталья Иванова',
            service: 'Экспресс-ответ',
            amount: '1 500 ₽',
            status: 'done',
            date: '06.03.2026'
          }, {
            id: 'ORD-8308',
            client: 'Виктория Смирнова',
            service: 'Натальная карта',
            amount: '4 500 ₽',
            status: 'progress',
            date: '05.03.2026'
          }, {
            id: 'ORD-8299',
            client: 'Андрей Попов',
            service: 'Консультация 60 мин',
            amount: '7 000 ₽',
            status: 'done',
            date: '04.03.2026'
          }, {
            id: 'ORD-8290',
            client: 'Екатерина Козлова',
            service: 'Подбор даты',
            amount: '1 800 ₽',
            status: 'done',
            date: '03.03.2026'
          }, {
            id: 'ORD-8281',
            client: 'Михаил Лебедев',
            service: 'Соляр',
            amount: '3 800 ₽',
            status: 'cancelled',
            date: '02.03.2026'
          }, {
            id: 'ORD-8275',
            client: 'Юлия Федорова',
            service: 'Натальная карта',
            amount: '4 500 ₽',
            status: 'done',
            date: '01.03.2026'
          }, {
            id: 'ORD-8260',
            client: 'Павел Николаев',
            service: 'Консультация 30 мин',
            amount: '3 500 ₽',
            status: 'done',
            date: '28.02.2026'
          }, {
            id: 'ORD-8251',
            client: 'Ирина Васильева',
            service: 'Совместимость',
            amount: '5 000 ₽',
            status: 'done',
            date: '27.02.2026'
          }, {
            id: 'ORD-8240',
            client: 'Роман Захаров',
            service: 'Транзитный прогноз',
            amount: '2 800 ₽',
            status: 'done',
            date: '26.02.2026'
          }, {
            id: 'ORD-8232',
            client: 'Светлана Орлова',
            service: 'Экспресс-ответ',
            amount: '1 500 ₽',
            status: 'done',
            date: '25.02.2026'
          }, {
            id: 'ORD-8220',
            client: 'Денис Макаров',
            service: 'Натальная карта',
            amount: '4 500 ₽',
            status: 'done',
            date: '24.02.2026'
          }];
          const filtered = mockOrders.filter(o => {
            const matchSearch = o.client.toLowerCase().includes(ordSearch.toLowerCase()) || o.id.toLowerCase().includes(ordSearch.toLowerCase()) || o.service.toLowerCase().includes(ordSearch.toLowerCase());
            const matchStatus = ordStatus === 'all' || o.status === ordStatus;
            return matchSearch && matchStatus;
          });
          const perPage = 8;
          const totalPages = Math.ceil(filtered.length / perPage);
          const paginated = filtered.slice((ordPage - 1) * perPage, ordPage * perPage);
          const doneCount = mockOrders.filter(o => o.status === 'done').length;
          const cancelledCount = mockOrders.filter(o => o.status === 'cancelled').length;
          return <div className="space-y-4" data-id="element-2127">
                  <div className="flex flex-wrap gap-4 mb-2" data-id="element-2128">
                    <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200" data-id="element-2129">
                      <div className="text-xs text-slate-500" data-id="element-2130">
                        Всего заказов
                      </div>
                      <div className="font-bold text-slate-900" data-id="element-2131">
                        {mockOrders.length}
                      </div>
                    </div>
                    <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-100" data-id="element-2132">
                      <div className="text-xs text-emerald-600" data-id="element-2133">Выполнено</div>
                      <div className="font-bold text-emerald-700" data-id="element-2134">
                        {doneCount}
                      </div>
                    </div>
                    <div className="bg-amber-50 px-4 py-2 rounded-lg border border-amber-100" data-id="element-2135">
                      <div className="text-xs text-amber-600" data-id="element-2136">В работе</div>
                      <div className="font-bold text-amber-700" data-id="element-2137">
                        {mockOrders.filter(o => o.status === 'progress').length}
                      </div>
                    </div>
                    <div className="bg-rose-50 px-4 py-2 rounded-lg border border-rose-100" data-id="element-2138">
                      <div className="text-xs text-rose-600" data-id="element-2139">Отменено</div>
                      <div className="font-bold text-rose-700" data-id="element-2140">
                        {cancelledCount}
                      </div>
                    </div>
                  </div>

                  {/* Топ услуг по заказам — визуальная сводка */}
                  {(() => {
              const serviceStats: Record<string, {
                count: number;
                revenue: number;
              }> = {};
              mockOrders.forEach(o => {
                if (!serviceStats[o.service]) serviceStats[o.service] = {
                  count: 0,
                  revenue: 0
                };
                serviceStats[o.service].count++;
                serviceStats[o.service].revenue += parseInt(o.amount.replace(/\D/g, '')) || 0;
              });
              const sorted = Object.entries(serviceStats).sort((a, b) => b[1].count - a[1].count);
              const maxCount = sorted[0]?.[1].count || 1;
              const totalRevenue = sorted.reduce((sum, [, s]) => sum + s.revenue, 0);
              const serviceColors: Record<string, string> = {
                'Натальная карта': 'bg-indigo-500',
                Соляр: 'bg-amber-500',
                Совместимость: 'bg-rose-500',
                'Консультация 60 мин': 'bg-purple-500',
                'Консультация 30 мин': 'bg-purple-400',
                'Транзитный прогноз': 'bg-teal-500',
                'Экспресс-ответ': 'bg-sky-500',
                'Подбор даты': 'bg-emerald-500'
              };
              return <Card padding="md" className="border-slate-200" data-id="element-2141">
                        <div className="flex items-center justify-between mb-4" data-id="element-2142">
                          <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-2143">
                            <ShoppingBag className="w-4 h-4 text-indigo-500" data-id="element-2144" />
                            Топ услуг по заказам
                          </h4>
                          <div className="text-xs text-slate-500" data-id="element-2145">
                            Общая выручка:{' '}
                            <span className="font-bold text-emerald-600" data-id="element-2146">
                              {totalRevenue.toLocaleString('ru-RU')} ₽
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5" data-id="element-2147">
                          {sorted.map(([service, stats], i) => <div key={service} className="flex items-center gap-3" data-id="element-2148">
                              <span className="text-[11px] text-slate-400 w-4 text-right font-medium" data-id="element-2149">
                                {i + 1}.
                              </span>
                              <div className="flex-1 min-w-0" data-id="element-2150">
                                <div className="flex justify-between items-baseline mb-1" data-id="element-2151">
                                  <span className="text-xs font-medium text-slate-700 truncate" data-id="element-2152">
                                    {service}
                                  </span>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2" data-id="element-2153">
                                    <span className="text-[10px] text-slate-500" data-id="element-2154">
                                      {stats.count} шт.
                                    </span>
                                    <span className="text-[10px] font-bold text-slate-700" data-id="element-2155">
                                      {stats.revenue.toLocaleString('ru-RU')} ₽
                                    </span>
                                  </div>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden" data-id="element-2156">
                                  <div className={`h-1.5 rounded-full transition-all ${serviceColors[service] || 'bg-slate-400'}`} style={{
                          width: `${stats.count / maxCount * 100}%`
                        }} data-id="element-2157" />
                                </div>
                              </div>
                            </div>)}
                        </div>
                      </Card>;
            })()}

                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-2158">
                    <div className="relative flex-1 max-w-md" data-id="element-2159">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2160" />
                      <input type="text" placeholder="Поиск по клиенту, ID, услуге..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" value={ordSearch} onChange={e => {
                  setOrdSearch(e.target.value);
                  setOrdPage(1);
                }} data-id="element-2161" />
                    </div>
                    <select className="rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={ordStatus} onChange={e => {
                setOrdStatus(e.target.value);
                setOrdPage(1);
              }} data-id="element-2162">
                      <option value="all" data-id="element-2163">Все статусы</option>
                      <option value="progress" data-id="element-2164">В работе</option>
                      <option value="done" data-id="element-2165">Выполнен</option>
                      <option value="cancelled" data-id="element-2166">Отменён</option>
                    </select>
                  </div>
                  <Card padding="none" className="overflow-hidden" data-id="element-2167">
                    <table className="w-full text-left" data-id="element-2168">
                      <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-2169">
                        <tr data-id="element-2170">
                          <th className="px-4 py-3 font-medium" data-id="element-2171">ID / Дата</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2172">Клиент</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2173">Услуга</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2174">Сумма</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2175">Статус</th>
                          <th className="px-4 py-3 text-right font-medium" data-id="element-2176">
                            Действия
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-2177">
                        {paginated.map(o => <tr key={o.id} className="hover:bg-slate-50" data-id="element-2178">
                            <td className="px-4 py-3" data-id="element-2179">
                              <div className="font-medium text-slate-900" data-id="element-2180">
                                #{o.id}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-2181">
                                {o.date}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700" data-id="element-2182">
                              {o.client}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700" data-id="element-2183">
                              {o.service}
                            </td>
                            <td className="px-4 py-3 font-bold text-slate-900" data-id="element-2184">
                              {o.amount}
                            </td>
                            <td className="px-4 py-3" data-id="element-2185">
                              <Badge variant={o.status === 'done' ? 'success' : o.status === 'progress' ? 'warning' : 'error'} size="sm" data-id="element-2186">
                                {o.status === 'done' ? 'Выполнен' : o.status === 'progress' ? 'В работе' : 'Отменён'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-2187">
                              <Button variant="ghost" size="sm" icon={<Eye className="w-3.5 h-3.5" data-id="element-2189" />} className="text-slate-400 hover:text-indigo-600" onClick={() => success(`Открыт заказ ${o.id}`)} data-id="element-2188" />
                            </td>
                          </tr>)}
                        {paginated.length === 0 && <tr data-id="element-2190">
                            <td colSpan={6} className="px-4 py-8 text-center text-slate-500" data-id="element-2191">
                              Ничего не найдено
                            </td>
                          </tr>}
                      </tbody>
                    </table>
                    {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center bg-slate-50/50" data-id="element-2192">
                        <Pagination currentPage={ordPage} totalPages={totalPages} onPageChange={setOrdPage} data-id="element-2193" />
                      </div>}
                  </Card>
                </div>;
        })()}

          {profileTab === 'consultations' && (() => {
          const mockConsultations = [{
            id: 1,
            client: 'Анна Ковалёва',
            format: 'Видео',
            duration: '60 мин',
            rating: 5.0,
            status: 'done',
            date: '15.03.2026, 14:00'
          }, {
            id: 2,
            client: 'Дмитрий Соколов',
            format: 'Видео',
            duration: '30 мин',
            rating: 4.8,
            status: 'done',
            date: '14.03.2026, 11:00'
          }, {
            id: 3,
            client: 'Елена Морозова',
            format: 'Чат',
            duration: '45 мин',
            rating: 0,
            status: 'active',
            date: 'Сейчас'
          }, {
            id: 4,
            client: 'Ольга Петрова',
            format: 'Видео',
            duration: '60 мин',
            rating: 5.0,
            status: 'done',
            date: '12.03.2026, 16:30'
          }, {
            id: 5,
            client: 'Игорь Волков',
            format: 'Аудио',
            duration: '30 мин',
            rating: 4.5,
            status: 'done',
            date: '11.03.2026, 10:00'
          }, {
            id: 6,
            client: 'Мария Лебедева',
            format: 'Видео',
            duration: '60 мин',
            rating: 0,
            status: 'cancelled',
            date: '10.03.2026, 15:00'
          }, {
            id: 7,
            client: 'Сергей Новиков',
            format: 'Аудио',
            duration: '30 мин',
            rating: 4.2,
            status: 'done',
            date: '09.03.2026, 12:00'
          }, {
            id: 8,
            client: 'Татьяна Романова',
            format: 'Видео',
            duration: '60 мин',
            rating: 4.9,
            status: 'done',
            date: '08.03.2026, 17:00'
          }, {
            id: 9,
            client: 'Наталья Иванова',
            format: 'Чат',
            duration: '20 мин',
            rating: 3.8,
            status: 'done',
            date: '07.03.2026, 09:30'
          }, {
            id: 10,
            client: 'Виктория Смирнова',
            format: 'Видео',
            duration: '60 мин',
            rating: 0,
            status: 'scheduled',
            date: 'Завтра, 14:00'
          }, {
            id: 11,
            client: 'Алексей Кузнецов',
            format: 'Видео',
            duration: '60 мин',
            rating: 0,
            status: 'scheduled',
            date: 'Завтра, 16:00'
          }, {
            id: 12,
            client: 'Андрей Попов',
            format: 'Аудио',
            duration: '30 мин',
            rating: 4.7,
            status: 'done',
            date: '05.03.2026, 11:30'
          }];
          const filtered = mockConsultations.filter(c => {
            const matchFormat = conFormat === 'all' || c.format === conFormat;
            return matchFormat;
          });
          const perPage = 6;
          const totalPages = Math.ceil(filtered.length / perPage);
          const paginated = filtered.slice((conPage - 1) * perPage, conPage * perPage);
          const doneList = mockConsultations.filter(c => c.status === 'done');
          const avgRat = doneList.length > 0 ? (doneList.reduce((a, c) => a + c.rating, 0) / doneList.length).toFixed(1) : '—';
          return <div className="space-y-4" data-id="element-2194">
                  <div className="flex flex-wrap gap-4 mb-2" data-id="element-2195">
                    <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200" data-id="element-2196">
                      <div className="text-xs text-slate-500" data-id="element-2197">Всего</div>
                      <div className="font-bold text-slate-900" data-id="element-2198">
                        {mockConsultations.length}
                      </div>
                    </div>
                    <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-100" data-id="element-2199">
                      <div className="text-xs text-emerald-600" data-id="element-2200">Завершено</div>
                      <div className="font-bold text-emerald-700" data-id="element-2201">
                        {doneList.length}
                      </div>
                    </div>
                    <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100" data-id="element-2202">
                      <div className="text-xs text-blue-600" data-id="element-2203">Запланировано</div>
                      <div className="font-bold text-blue-700" data-id="element-2204">
                        {mockConsultations.filter(c => c.status === 'scheduled').length}
                      </div>
                    </div>
                    <div className="bg-amber-50 px-4 py-2 rounded-lg border border-amber-100" data-id="element-2205">
                      <div className="text-xs text-amber-600" data-id="element-2206">Ср. оценка</div>
                      <div className="font-bold text-amber-700" data-id="element-2207">{avgRat}</div>
                    </div>
                  </div>
                  <div className="flex gap-3" data-id="element-2208">
                    <select className="rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={conFormat} onChange={e => {
                setConFormat(e.target.value);
                setConPage(1);
              }} data-id="element-2209">
                      <option value="all" data-id="element-2210">Все форматы</option>
                      <option value="Видео" data-id="element-2211">Видео</option>
                      <option value="Аудио" data-id="element-2212">Аудио</option>
                      <option value="Чат" data-id="element-2213">Чат</option>
                    </select>
                  </div>
                  <Card padding="none" className="overflow-hidden" data-id="element-2214">
                    <table className="w-full text-left" data-id="element-2215">
                      <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-2216">
                        <tr data-id="element-2217">
                          <th className="px-4 py-3 font-medium" data-id="element-2218">
                            Дата / Время
                          </th>
                          <th className="px-4 py-3 font-medium" data-id="element-2219">Клиент</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2220">Формат</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2221">
                            Длительность
                          </th>
                          <th className="px-4 py-3 font-medium" data-id="element-2222">Оценка</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2223">Статус</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-2224">
                        {paginated.map(c => <tr key={c.id} className="hover:bg-slate-50" data-id="element-2225">
                            <td className="px-4 py-3 font-medium text-slate-900 text-sm" data-id="element-2226">
                              {c.date}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700" data-id="element-2227">
                              {c.client}
                            </td>
                            <td className="px-4 py-3" data-id="element-2228">
                              <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-2229">
                                {c.format === 'Видео' ? <Video className="w-4 h-4 text-blue-500" data-id="element-2230" /> : c.format === 'Аудио' ? <Phone className="w-4 h-4 text-emerald-500" data-id="element-2231" /> : <MessageCircle className="w-4 h-4 text-amber-500" data-id="element-2232" />}
                                {c.format}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700" data-id="element-2233">
                              {c.duration}
                            </td>
                            <td className="px-4 py-3" data-id="element-2234">
                              {c.rating > 0 ? <div className="flex items-center gap-1" data-id="element-2235">
                                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3 h-3 ${s <= Math.round(c.rating) ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} data-id="element-2236" />)}
                                  <span className="text-xs text-slate-500 ml-1" data-id="element-2237">
                                    {c.rating}
                                  </span>
                                </div> : <span className="text-xs text-slate-400" data-id="element-2238">
                                  —
                                </span>}
                            </td>
                            <td className="px-4 py-3" data-id="element-2239">
                              <Badge variant={c.status === 'done' ? 'success' : c.status === 'active' ? 'primary' : c.status === 'scheduled' ? 'neutral' : 'error'} size="sm" data-id="element-2240">
                                {c.status === 'done' ? 'Завершена' : c.status === 'active' ? 'В процессе' : c.status === 'scheduled' ? 'Запланирована' : 'Отменена'}
                              </Badge>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                    {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center bg-slate-50/50" data-id="element-2241">
                        <Pagination currentPage={conPage} totalPages={totalPages} onPageChange={setConPage} data-id="element-2242" />
                      </div>}
                  </Card>
                </div>;
        })()}

          {profileTab === 'complaints' && (() => {
          const mockComplaints = [{
            id: 1,
            client: 'Мария Лебедева',
            reason: 'Астролог предлагал перейти в WhatsApp для общения и оплаты мимо платформы.',
            severity: 'high',
            status: 'open',
            date: '14.03.2026'
          }, {
            id: 2,
            client: 'Сергей Новиков',
            reason: 'Консультация была проведена с опозданием на 25 минут без предупреждения.',
            severity: 'medium',
            status: 'resolved',
            date: '10.03.2026'
          }, {
            id: 3,
            client: 'Татьяна Романова',
            reason: 'Качество отчёта не соответствует описанию — слишком поверхностный анализ.',
            severity: 'medium',
            status: 'dismissed',
            date: '05.03.2026'
          }, {
            id: 4,
            client: 'Наталья Иванова',
            reason: 'Грубое общение во время консультации, астролог был нетерпелив.',
            severity: 'high',
            status: 'open',
            date: '02.03.2026'
          }, {
            id: 5,
            client: 'Андрей Попов',
            reason: 'Астролог отправил шаблонный отчёт, не учитывая мои данные рождения.',
            severity: 'medium',
            status: 'resolved',
            date: '25.02.2026'
          }, {
            id: 6,
            client: 'Екатерина Козлова',
            reason: 'Попытка продать дополнительные услуги вне платформы.',
            severity: 'high',
            status: 'open',
            date: '20.02.2026'
          }, {
            id: 7,
            client: 'Виктория Смирнова',
            reason: 'Отчёт был доставлен с задержкой в 5 дней.',
            severity: 'low',
            status: 'resolved',
            date: '15.02.2026'
          }, {
            id: 8,
            client: 'Юлия Федорова',
            reason: 'Астролог не вышел на видеоконсультацию в назначенное время.',
            severity: 'medium',
            status: 'resolved',
            date: '10.02.2026'
          }];
          const filtered = mockComplaints.filter(c => compStatus === 'all' || c.status === compStatus);
          const perPage = 5;
          const totalPages = Math.ceil(filtered.length / perPage);
          const paginated = filtered.slice((compPage - 1) * perPage, compPage * perPage);
          const openCount = mockComplaints.filter(c => c.status === 'open').length;
          const resolvedCount = mockComplaints.filter(c => c.status === 'resolved').length;
          const dismissedCount = mockComplaints.filter(c => c.status === 'dismissed').length;
          return <div className="space-y-4" data-id="element-2243">
                  <div className="flex flex-wrap gap-4 mb-2" data-id="element-2244">
                    <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200" data-id="element-2245">
                      <div className="text-xs text-slate-500" data-id="element-2246">Всего жалоб</div>
                      <div className="font-bold text-slate-900" data-id="element-2247">
                        {mockComplaints.length}
                      </div>
                    </div>
                    <div className="bg-rose-50 px-4 py-2 rounded-lg border border-rose-100" data-id="element-2248">
                      <div className="text-xs text-rose-600" data-id="element-2249">Открытых</div>
                      <div className="font-bold text-rose-700" data-id="element-2250">{openCount}</div>
                    </div>
                    <div className="bg-emerald-50 px-4 py-2 rounded-lg border border-emerald-100" data-id="element-2251">
                      <div className="text-xs text-emerald-600" data-id="element-2252">Решённых</div>
                      <div className="font-bold text-emerald-700" data-id="element-2253">
                        {resolvedCount}
                      </div>
                    </div>
                    <div className="bg-slate-50 px-4 py-2 rounded-lg border border-slate-200" data-id="element-2254">
                      <div className="text-xs text-slate-500" data-id="element-2255">Отклонённых</div>
                      <div className="font-bold text-slate-900" data-id="element-2256">
                        {dismissedCount}
                      </div>
                    </div>
                  </div>
                  <select className="rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={compStatus} onChange={e => {
              setCompStatus(e.target.value);
              setCompPage(1);
            }} data-id="element-2257">
                    <option value="all" data-id="element-2258">Все статусы</option>
                    <option value="open" data-id="element-2259">Открытые</option>
                    <option value="resolved" data-id="element-2260">Решённые</option>
                    <option value="dismissed" data-id="element-2261">Отклонённые</option>
                  </select>
                  <div className="grid gap-4" data-id="element-2262">
                    {paginated.map(c => <Card key={c.id} padding="md" className={`border-l-4 ${c.severity === 'high' ? 'border-l-rose-500 bg-rose-50/20' : c.severity === 'medium' ? 'border-l-amber-500 bg-amber-50/20' : 'border-l-slate-300'}`} data-id="element-2263">
                        <div className="flex justify-between items-start mb-3" data-id="element-2264">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-2265">
                            <Badge variant={c.status === 'open' ? 'error' : c.status === 'resolved' ? 'success' : 'neutral'} size="sm" data-id="element-2266">
                              {c.status === 'open' ? 'Открыта' : c.status === 'resolved' ? 'Решена' : 'Отклонена'}
                            </Badge>
                            <Badge variant={c.severity === 'high' ? 'error' : c.severity === 'medium' ? 'warning' : 'neutral'} size="sm" data-id="element-2267">
                              {c.severity === 'high' ? 'Высокая' : c.severity === 'medium' ? 'Средняя' : 'Низкая'}
                            </Badge>
                            <span className="text-sm font-medium text-slate-900" data-id="element-2268">
                              от {c.client}
                            </span>
                            <span className="text-xs text-slate-500" data-id="element-2269">
                              {c.date}
                            </span>
                          </div>
                          {c.status === 'open' && <Button variant="outline" size="sm" onClick={() => success('Жалоба взята в работу')} data-id="element-2270">
                              Разобрать
                            </Button>}
                        </div>
                        <p className="text-sm text-slate-700 bg-white p-3 rounded-lg border border-slate-200" data-id="element-2271">
                          {c.reason}
                        </p>
                      </Card>)}
                    {paginated.length === 0 && <div className="flex flex-col items-center justify-center h-40 text-slate-500" data-id="element-2272">
                        <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-2" data-id="element-2273" />
                        <p className="font-medium" data-id="element-2274">
                          Нет жалоб с таким статусом
                        </p>
                      </div>}
                  </div>
                  {totalPages > 1 && <div className="flex justify-center" data-id="element-2275">
                      <Pagination currentPage={compPage} totalPages={totalPages} onPageChange={setCompPage} data-id="element-2276" />
                    </div>}
                </div>;
        })()}

          {profileTab === 'finance' && (() => {
          const months = [{
            label: 'Окт',
            income: 45000
          }, {
            label: 'Ноя',
            income: 52000
          }, {
            label: 'Дек',
            income: 68000
          }, {
            label: 'Янв',
            income: 71000
          }, {
            label: 'Фев',
            income: 83000
          }, {
            label: 'Мар',
            income: 62000
          }];
          const maxIncome = Math.max(...months.map(m => m.income));
          const payouts = [{
            period: 'Март 2026',
            orders: 12,
            consult: 5,
            total: '62 000 ₽',
            commission: '12 400 ₽',
            payout: '49 600 ₽',
            status: 'pending'
          }, {
            period: 'Февраль 2026',
            orders: 15,
            consult: 7,
            total: '83 000 ₽',
            commission: '16 600 ₽',
            payout: '66 400 ₽',
            status: 'paid'
          }, {
            period: 'Январь 2026',
            orders: 13,
            consult: 6,
            total: '71 000 ₽',
            commission: '14 200 ₽',
            payout: '56 800 ₽',
            status: 'paid'
          }, {
            period: 'Декабрь 2025',
            orders: 14,
            consult: 8,
            total: '68 000 ₽',
            commission: '13 600 ₽',
            payout: '54 400 ₽',
            status: 'paid'
          }, {
            period: 'Ноябрь 2025',
            orders: 10,
            consult: 4,
            total: '52 000 ₽',
            commission: '10 400 ₽',
            payout: '41 600 ₽',
            status: 'paid'
          }, {
            period: 'Октябрь 2025',
            orders: 9,
            consult: 3,
            total: '45 000 ₽',
            commission: '9 000 ₽',
            payout: '36 000 ₽',
            status: 'paid'
          }, {
            period: 'Сентябрь 2025',
            orders: 11,
            consult: 5,
            total: '58 000 ₽',
            commission: '11 600 ₽',
            payout: '46 400 ₽',
            status: 'paid'
          }, {
            period: 'Август 2025',
            orders: 8,
            consult: 3,
            total: '41 000 ₽',
            commission: '8 200 ₽',
            payout: '32 800 ₽',
            status: 'paid'
          }];
          const perPage = 5;
          const totalPages = Math.ceil(payouts.length / perPage);
          const paginated = payouts.slice((finPage - 1) * perPage, finPage * perPage);
          return <div className="space-y-6" data-id="element-2277">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2278">
                    <Card padding="md" className="bg-emerald-50 border-emerald-100" data-id="element-2279">
                      <div className="text-xs text-emerald-700 font-medium mb-1" data-id="element-2280">
                        Общий доход
                      </div>
                      <div className="text-2xl font-bold text-emerald-600" data-id="element-2281">
                        445 000 ₽
                      </div>
                    </Card>
                    <Card padding="md" data-id="element-2282">
                      <div className="text-xs text-slate-500 font-medium mb-1" data-id="element-2283">
                        Комиссия (20%)
                      </div>
                      <div className="text-2xl font-bold text-slate-900" data-id="element-2284">
                        89 000 ₽
                      </div>
                    </Card>
                    <Card padding="md" data-id="element-2285">
                      <div className="text-xs text-slate-500 font-medium mb-1" data-id="element-2286">
                        Выплачено
                      </div>
                      <div className="text-2xl font-bold text-slate-900" data-id="element-2287">
                        356 000 ₽
                      </div>
                    </Card>
                    <Card padding="md" className="bg-indigo-50 border-indigo-100" data-id="element-2288">
                      <div className="text-xs text-indigo-700 font-medium mb-1" data-id="element-2289">
                        К выводу
                      </div>
                      <div className="text-2xl font-bold text-indigo-600" data-id="element-2290">
                        49 600 ₽
                      </div>
                    </Card>
                  </div>

                  <Card padding="md" data-id="element-2291">
                    <h4 className="font-bold text-slate-900 mb-4" data-id="element-2292">
                      Доход по месяцам
                    </h4>
                    <div className="flex items-end gap-3 h-36" data-id="element-2293">
                      {months.map((m, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-2294">
                          <div className="text-xs font-bold text-slate-700" data-id="element-2295">
                            {(m.income / 1000).toFixed(0)}K
                          </div>
                          <div className="w-full rounded-t-md bg-indigo-500 transition-all" style={{
                    height: `${m.income / maxIncome * 100}%`
                  }} data-id="element-2296" />
                          <div className="text-[10px] text-slate-500 font-medium" data-id="element-2297">
                            {m.label}
                          </div>
                        </div>)}
                    </div>
                  </Card>

                  <Card padding="md" data-id="element-2298">
                    <h4 className="font-bold text-slate-900 mb-4" data-id="element-2299">
                      История выплат
                    </h4>
                    <table className="w-full text-left" data-id="element-2300">
                      <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-2301">
                        <tr data-id="element-2302">
                          <th className="px-4 py-3 font-medium" data-id="element-2303">Период</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2304">Заказы</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2305">Консульт.</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2306">Итого</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2307">Комиссия</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2308">К выплате</th>
                          <th className="px-4 py-3 font-medium" data-id="element-2309">Статус</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-2310">
                        {paginated.map((p, i) => <tr key={i} className="hover:bg-slate-50" data-id="element-2311">
                            <td className="px-4 py-3 font-medium text-slate-900 text-sm" data-id="element-2312">
                              {p.period}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600" data-id="element-2313">
                              {p.orders}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600" data-id="element-2314">
                              {p.consult}
                            </td>
                            <td className="px-4 py-3 font-bold text-slate-900" data-id="element-2315">
                              {p.total}
                            </td>
                            <td className="px-4 py-3 text-sm text-rose-600" data-id="element-2316">
                              {p.commission}
                            </td>
                            <td className="px-4 py-3 font-bold text-emerald-600" data-id="element-2317">
                              {p.payout}
                            </td>
                            <td className="px-4 py-3" data-id="element-2318">
                              <Badge variant={p.status === 'paid' ? 'success' : 'warning'} size="sm" data-id="element-2319">
                                {p.status === 'paid' ? 'Выплачено' : 'Ожидает'}
                              </Badge>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                    {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center bg-slate-50/50" data-id="element-2320">
                        <Pagination currentPage={finPage} totalPages={totalPages} onPageChange={setFinPage} data-id="element-2321" />
                      </div>}
                  </Card>
                </div>;
        })()}

          {profileTab === 'reviews' && (() => {
          const mockReviews = [{
            id: 1,
            client: 'Анна Ковалёва',
            rating: 5,
            service: 'Натальная карта',
            text: 'Потрясающий разбор! Очень точно и подробно, ответила на все вопросы. Обязательно обращусь ещё.',
            date: '15.03.2026'
          }, {
            id: 2,
            client: 'Дмитрий Соколов',
            rating: 5,
            service: 'Соляр',
            text: 'Отличная консультация, всё по делу. Прогноз на год очень детальный.',
            date: '14.03.2026'
          }, {
            id: 3,
            client: 'Ольга Петрова',
            rating: 4,
            service: 'Консультация 60 мин',
            text: 'Хороший специалист, но немного затянула с началом консультации.',
            date: '12.03.2026'
          }, {
            id: 4,
            client: 'Игорь Волков',
            rating: 5,
            service: 'Транзитный прогноз',
            text: 'Рекомендую! Прогноз оказался очень точным, всё сбылось.',
            date: '11.03.2026'
          }, {
            id: 5,
            client: 'Мария Лебедева',
            rating: 2,
            service: 'Совместимость',
            text: 'Не понравилось — пыталась увести в мессенджер для оплаты. Обратилась в поддержку.',
            date: '10.03.2026'
          }, {
            id: 6,
            client: 'Сергей Новиков',
            rating: 4,
            service: 'Консультация 30 мин',
            text: 'В целом хорошо, но хотелось бы больше конкретики в рекомендациях.',
            date: '09.03.2026'
          }, {
            id: 7,
            client: 'Татьяна Романова',
            rating: 5,
            service: 'Натальная карта',
            text: 'Лучший астролог на платформе! Очень глубокий и точный анализ.',
            date: '08.03.2026'
          }, {
            id: 8,
            client: 'Наталья Иванова',
            rating: 3,
            service: 'Экспресс-ответ',
            text: 'Ответ был слишком общим, ожидала большей детализации за эту цену.',
            date: '07.03.2026'
          }, {
            id: 9,
            client: 'Алексей Кузнецов',
            rating: 5,
            service: 'Совместимость',
            text: 'Всё супер! Подробный разбор совместимости, помог разобраться в отношениях.',
            date: '06.03.2026'
          }, {
            id: 10,
            client: 'Виктория Смирнова',
            rating: 4,
            service: 'Соляр',
            text: 'Хороший прогноз, но доставка заняла чуть больше обещанного.',
            date: '05.03.2026'
          }, {
            id: 11,
            client: 'Андрей Попов',
            rating: 5,
            service: 'Консультация 60 мин',
            text: 'Превосходная консультация! Мария очень внимательна и профессиональна.',
            date: '04.03.2026'
          }, {
            id: 12,
            client: 'Екатерина Козлова',
            rating: 1,
            service: 'Натальная карта',
            text: 'Получила шаблонный отчёт, который не учитывал мои данные. Разочарована.',
            date: '03.03.2026'
          }, {
            id: 13,
            client: 'Юлия Федорова',
            rating: 5,
            service: 'Подбор даты',
            text: 'Идеально подобрала дату для свадьбы! Всё прошло замечательно.',
            date: '02.03.2026'
          }, {
            id: 14,
            client: 'Павел Николаев',
            rating: 4,
            service: 'Транзитный прогноз',
            text: 'Хороший прогноз, достаточно подробный. Буду обращаться ещё.',
            date: '01.03.2026'
          }, {
            id: 15,
            client: 'Светлана Орлова',
            rating: 5,
            service: 'Консультация 60 мин',
            text: 'Замечательная консультация! Очень помогла разобраться в сложной ситуации.',
            date: '28.02.2026'
          }];
          const dist = [5, 4, 3, 2, 1].map(r => ({
            rating: r,
            count: mockReviews.filter(rv => rv.rating === r).length
          }));
          const maxCount = Math.max(...dist.map(d => d.count));
          const filtered = mockReviews.filter(r => revRating === 'all' || r.rating === parseInt(revRating));
          const perPage = 5;
          const totalPages = Math.ceil(filtered.length / perPage);
          const paginated = filtered.slice((revPage - 1) * perPage, revPage * perPage);
          const avgRating = (mockReviews.reduce((a, r) => a + r.rating, 0) / mockReviews.length).toFixed(1);
          return <div className="space-y-4" data-id="element-2322">
                  <div className="flex flex-col md:flex-row gap-6 mb-4" data-id="element-2323">
                    <div className="flex items-center gap-4" data-id="element-2324">
                      <div className="text-5xl font-black text-slate-900" data-id="element-2325">
                        {avgRating}
                      </div>
                      <div data-id="element-2326">
                        <div className="flex text-amber-500 mb-1" data-id="element-2327">
                          {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 ${s <= Math.round(parseFloat(avgRating)) ? 'fill-amber-500 text-amber-500' : 'text-slate-300'}`} data-id="element-2328" />)}
                        </div>
                        <div className="text-sm text-slate-500" data-id="element-2329">
                          {mockReviews.length} отзывов
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 space-y-1.5" data-id="element-2330">
                      {dist.map(d => <div key={d.rating} className="flex items-center gap-2 cursor-pointer group" onClick={() => {
                  setRevRating(revRating === String(d.rating) ? 'all' : String(d.rating));
                  setRevPage(1);
                }} data-id="element-2331">
                          <span className="text-xs font-medium text-slate-600 w-4" data-id="element-2332">
                            {d.rating}★
                          </span>
                          <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-2333">
                            <div className={`h-full rounded-full transition-all ${revRating === String(d.rating) ? 'bg-indigo-500' : 'bg-amber-400'} group-hover:bg-indigo-500`} style={{
                      width: maxCount > 0 ? `${d.count / maxCount * 100}%` : '0%'
                    }} data-id="element-2334" />
                          </div>
                          <span className="text-xs text-slate-500 w-6 text-right" data-id="element-2335">
                            {d.count}
                          </span>
                        </div>)}
                    </div>
                  </div>

                  {revRating !== 'all' && <div className="flex items-center gap-2" data-id="element-2336">
                      <Badge variant="primary" size="sm" data-id="element-2337">
                        Фильтр: {revRating}★
                      </Badge>
                      <button className="text-xs text-indigo-600 hover:underline" onClick={() => {
                setRevRating('all');
                setRevPage(1);
              }} data-id="element-2338">
                        Сбросить
                      </button>
                    </div>}

                  <div className="grid gap-4" data-id="element-2339">
                    {paginated.map(r => <Card key={r.id} padding="md" data-id="element-2340">
                        <div className="flex justify-between items-start mb-2" data-id="element-2341">
                          <div className="flex items-center gap-2" data-id="element-2342">
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-2343">
                              {r.client.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div data-id="element-2344">
                              <div className="text-sm font-medium text-slate-900" data-id="element-2345">
                                {r.client}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-2346">
                                {r.date} • {r.service}
                              </div>
                            </div>
                          </div>
                          <div className="flex" data-id="element-2347">
                            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} data-id="element-2348" />)}
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 mt-2" data-id="element-2349">{r.text}</p>
                      </Card>)}
                    {paginated.length === 0 && <div className="text-center py-8 text-slate-500 text-sm" data-id="element-2350">
                        Нет отзывов с таким рейтингом
                      </div>}
                  </div>
                  {totalPages > 1 && <div className="flex justify-center" data-id="element-2351">
                      <Pagination currentPage={revPage} totalPages={totalPages} onPageChange={setRevPage} data-id="element-2352" />
                    </div>}
                </div>;
        })()}

          {profileTab === 'risk' && <div className="space-y-6" data-id="element-2353">
              <div className="flex flex-col md:flex-row gap-6" data-id="element-2354">
                <Card padding="lg" className={`flex-1 border-2 ${selectedAstrologer.riskScore === 'high' ? 'border-rose-500 bg-rose-50' : selectedAstrologer.riskScore === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'}`} data-id="element-2355">
                  <div className="flex items-center justify-between mb-4" data-id="element-2356">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-2357">
                      Risk Score
                    </h3>
                    <Badge variant={selectedAstrologer.riskScore === 'high' ? 'error' : selectedAstrologer.riskScore === 'medium' ? 'warning' : 'success'} className="text-sm px-3 py-1" data-id="element-2358">
                      {selectedAstrologer.riskScore === 'high' ? 'КРИТИЧЕСКИЙ' : selectedAstrologer.riskScore === 'medium' ? 'ПОВЫШЕННЫЙ' : 'НОРМАЛЬНЫЙ'}
                    </Badge>
                  </div>
                  <div className="text-4xl font-black mb-2 text-slate-900" data-id="element-2359">
                    {selectedAstrologer.riskScore === 'high' ? '85' : selectedAstrologer.riskScore === 'medium' ? '45' : '12'}{' '}
                    <span className="text-lg text-slate-500 font-medium" data-id="element-2360">
                      / 100
                    </span>
                  </div>
                  <p className="text-sm text-slate-700" data-id="element-2361">
                    {selectedAstrologer.riskScore === 'high' ? 'Высокая вероятность нарушения правил платформы. Рекомендуется ограничить доступ.' : 'Показатели в норме. Продолжайте наблюдение.'}
                  </p>
                </Card>

                <div className="flex-1 space-y-3" data-id="element-2362">
                  <h4 className="font-bold text-slate-900" data-id="element-2363">Сводка нарушений</h4>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-slate-200" data-id="element-2364">
                    <span className="text-sm text-slate-700" data-id="element-2365">
                      Попытки увода клиентов
                    </span>
                    <span className={`font-bold ${selectedAstrologer.riskScore === 'high' ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-2366">
                      {selectedAstrologer.riskScore === 'high' ? '2' : '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-slate-200" data-id="element-2367">
                    <span className="text-sm text-slate-700" data-id="element-2368">
                      Жалобы пользователей
                    </span>
                    <span className={`font-bold ${selectedAstrologer.complaintsCount > 0 ? 'text-amber-600' : 'text-slate-900'}`} data-id="element-2369">
                      {selectedAstrologer.complaintsCount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-slate-200" data-id="element-2370">
                    <span className="text-sm text-slate-700" data-id="element-2371">
                      Отмены заказов
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-2372">1</span>
                  </div>
                </div>
              </div>

              <Card padding="md" data-id="element-2373">
                <h4 className="font-bold text-slate-900 mb-4" data-id="element-2374">
                  Журнал событий безопасности
                </h4>
                <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-2375">
                  {selectedAstrologer.riskScore === 'high' && <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2376">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-rose-100 text-rose-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2377">
                        <AlertTriangle className="w-5 h-5" data-id="element-2378" />
                      </div>
                      <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-rose-200 shadow-sm" data-id="element-2379">
                        <div className="flex items-center justify-between mb-1" data-id="element-2380">
                          <div className="font-bold text-rose-900 text-sm" data-id="element-2381">
                            Обнаружен обмен контактами
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-2382">
                            Сегодня, 14:30
                          </div>
                        </div>
                        <div className="text-sm text-slate-700" data-id="element-2383">
                          Система зафиксировала ключевое слово "whatsapp" в чате
                          с Клиентом #8401.
                        </div>
                      </div>
                    </div>}
                  {selectedAstrologer.complaintsCount > 0 && <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2384">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-amber-100 text-amber-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2385">
                        <AlertTriangle className="w-5 h-5" data-id="element-2386" />
                      </div>
                      <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-2387">
                        <div className="flex items-center justify-between mb-1" data-id="element-2388">
                          <div className="font-bold text-slate-900 text-sm" data-id="element-2389">
                            Новая жалоба
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-2390">
                            Вчера, 15:40
                          </div>
                        </div>
                        <div className="text-sm text-slate-700" data-id="element-2391">
                          Пользователь пожаловался на качество консультации.
                        </div>
                      </div>
                    </div>}
                  <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2392">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-emerald-100 text-emerald-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2393">
                      <CheckCircle2 className="w-5 h-5" data-id="element-2394" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl border border-slate-200 shadow-sm" data-id="element-2395">
                      <div className="flex items-center justify-between mb-1" data-id="element-2396">
                        <div className="font-bold text-slate-900 text-sm" data-id="element-2397">
                          Успешная верификация
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-2398">
                          {selectedAstrologer.regDate}
                        </div>
                      </div>
                      <div className="text-sm text-slate-700" data-id="element-2399">
                        Документы проверены, профиль активирован.
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>}
        </div>
      </motion.div>;
  }
  // ==========================================
  // MAIN DASHBOARD VIEW
  // ==========================================
  return <div className="space-y-6" data-id="element-2400">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2401">
        <div data-id="element-2402">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-2403">
            Астрологи (Маркетплейс)
          </h2>
          <p className="text-sm text-slate-500" data-id="element-2404">
            Управление специалистами, контроль качества и мониторинг рисков
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" data-id="element-2406" />} onClick={() => setCreatePanel({
        type: 'astrologer',
        title: 'Добавить астролога'
      })} data-id="element-2405">
          Добавить астролога
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-id="element-2407">
        {renderKpiCard('Всего', totalCount, <Star className="w-4 h-4 text-indigo-600" data-id="element-2408" />, 'bg-indigo-50', 'text-indigo-600', {
        status: 'all'
      })}
        {renderKpiCard('Онлайн сейчас', onlineCount, <Globe className="w-4 h-4 text-emerald-600" data-id="element-2409" />, 'bg-emerald-50', 'text-emerald-600', {
        status: 'active'
      })}
        {renderKpiCard('Активные', activeCount, <UserCheck className="w-4 h-4 text-blue-600" data-id="element-2410" />, 'bg-blue-50', 'text-blue-600', {
        status: 'active'
      })}
        {renderKpiCard('Новые заявки', pendingCount, <Clock className="w-4 h-4 text-amber-600" data-id="element-2411" />, 'bg-amber-50', 'text-amber-600', {
        status: 'pending'
      })}
        {renderKpiCard('С жалобами', complaintsCount, <AlertTriangle className="w-4 h-4 text-rose-600" data-id="element-2412" />, 'bg-rose-50', 'text-rose-600', {
        complaints: 'yes'
      })}
        {renderKpiCard('Заблокированы', blockedCount, <Ban className="w-4 h-4 text-slate-600" data-id="element-2413" />, 'bg-slate-100', 'text-slate-600', {
        status: 'blocked'
      })}
        {renderKpiCard('Ср. рейтинг', avgRating, <Star className="w-4 h-4 text-amber-500" data-id="element-2414" />, 'bg-amber-50', 'text-amber-600')}
      </div>

      {/* Filter Bar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-2415">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2416">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-2417">
            <div className="relative flex-1 max-w-md" data-id="element-2418">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2419" />
              <input type="text" placeholder="Поиск по имени, email, специализации..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2420" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-2422" />} onClick={() => setShowFilters(!showFilters)} data-id="element-2421">
              Фильтры
            </Button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-2423">
            <select className="rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={filters.sort} onChange={e => setFilters({
            ...filters,
            sort: e.target.value
          })} data-id="element-2424">
              <option value="newest" data-id="element-2425">Сначала новые</option>
              <option value="income_desc" data-id="element-2426">По доходу (убывание)</option>
              <option value="rating_desc" data-id="element-2427">По рейтингу (убывание)</option>
              <option value="orders_desc" data-id="element-2428">По заказам (убывание)</option>
            </select>
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-2430" />} onClick={handleExport} data-id="element-2429">
              Экспорт
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        <AnimatePresence data-id="element-2431">
          {showFilters && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="mt-4 pt-4 border-t border-slate-100 overflow-hidden" data-id="element-2432">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4" data-id="element-2433">
                <div data-id="element-2434">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2435">
                    Статус
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={filters.status} onChange={e => setFilters({
                ...filters,
                status: e.target.value
              })} data-id="element-2436">
                    <option value="all" data-id="element-2437">Все</option>
                    <option value="active" data-id="element-2438">Активные</option>
                    <option value="pending" data-id="element-2439">Ожидают</option>
                    <option value="blocked" data-id="element-2440">Заблокированные</option>
                  </select>
                </div>
                <div data-id="element-2441">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2442">
                    Специализация
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={filters.spec} onChange={e => setFilters({
                ...filters,
                spec: e.target.value
              })} data-id="element-2443">
                    <option value="all" data-id="element-2444">Все</option>
                    <option value="Западная" data-id="element-2445">Западная астрология</option>
                    <option value="Ведическая" data-id="element-2446">Ведическая астрология</option>
                    <option value="Таро" data-id="element-2447">Таро</option>
                  </select>
                </div>
                <div data-id="element-2448">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2449">
                    Жалобы
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={filters.complaints} onChange={e => setFilters({
                ...filters,
                complaints: e.target.value
              })} data-id="element-2450">
                    <option value="all" data-id="element-2451">Все</option>
                    <option value="yes" data-id="element-2452">Есть жалобы</option>
                    <option value="no" data-id="element-2453">Нет жалоб</option>
                  </select>
                </div>
                <div data-id="element-2454">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-2455">
                    Risk Score
                  </label>
                  <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500" value={filters.risk} onChange={e => setFilters({
                ...filters,
                risk: e.target.value
              })} data-id="element-2456">
                    <option value="all" data-id="element-2457">Все</option>
                    <option value="high" data-id="element-2458">Высокий риск</option>
                    <option value="medium" data-id="element-2459">Средний риск</option>
                    <option value="low" data-id="element-2460">Низкий риск</option>
                  </select>
                </div>
                <div className="flex items-end" data-id="element-2461">
                  <Button variant="ghost" className="w-full text-slate-500 hover:bg-slate-100" onClick={() => setFilters({
                status: 'all',
                verified: 'all',
                spec: 'all',
                complaints: 'all',
                risk: 'all',
                sort: 'newest'
              })} data-id="element-2462">
                    Сбросить фильтры
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Card>

      {/* Mass Actions Bar */}
      <AnimatePresence data-id="element-2463">
        {selectedIds.size > 0 && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center justify-between shadow-sm" data-id="element-2464">
            <div className="flex items-center gap-3" data-id="element-2465">
              <Badge variant="primary" className="bg-indigo-600 text-white border-none" data-id="element-2466">
                Выбрано: {selectedIds.size}
              </Badge>
              <span className="text-sm text-indigo-800 font-medium" data-id="element-2467">
                Массовые действия:
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-2468">
              <Button variant="outline" size="sm" className="bg-white text-slate-700 hover:bg-slate-50" icon={<Mail className="w-3.5 h-3.5" data-id="element-2470" />} onClick={() => handleMassAction('message')} data-id="element-2469">
                Написать
              </Button>
              <Button variant="outline" size="sm" className="bg-white text-rose-600 hover:bg-rose-50 border-rose-200" icon={<Ban className="w-3.5 h-3.5" data-id="element-2472" />} onClick={() => handleMassAction('block')} data-id="element-2471">
                Заблокировать
              </Button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Data Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-visible" data-id="element-2473">
        <div className="overflow-x-auto min-h-[400px]" data-id="element-2474">
          <table className="w-full text-left border-collapse" data-id="element-2475">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-2476">
              <tr data-id="element-2477">
                <th className="px-4 py-3 w-10" data-id="element-2478">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={selectedIds.size === currentAstrologers.length && currentAstrologers.length > 0} onChange={toggleSelectAll} data-id="element-2479" />
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-2480">Астролог</th>
                <th className="px-4 py-3 font-medium" data-id="element-2481">Специализация</th>
                <th className="px-4 py-3 font-medium" data-id="element-2482">Статистика</th>
                <th className="px-4 py-3 font-medium" data-id="element-2483">Доход</th>
                <th className="px-4 py-3 font-medium" data-id="element-2484">Статус & Риски</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-2485">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2486">
              {currentAstrologers.map(a => <tr key={a.id} className={`hover:bg-slate-50 transition-colors group ${selectedIds.has(a.id) ? 'bg-indigo-50/30' : ''} ${a.status === 'pending' ? 'bg-amber-50/10' : ''}`} data-id="element-2487">
                  <td className="px-4 py-3" data-id="element-2488">
                    <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={selectedIds.has(a.id)} onChange={() => toggleSelect(a.id)} data-id="element-2489" />
                  </td>
                  <td className="px-4 py-3" data-id="element-2490">
                    <div className="flex items-center gap-3" data-id="element-2491">
                      <div className="relative" data-id="element-2492">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm border border-slate-200" data-id="element-2493">
                          {a.name.split(' ').map((n: string) => n[0]).join('')}
                        </div>
                        {a.isOnline && <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" data-id="element-2494"></div>}
                      </div>
                      <div data-id="element-2495">
                        <div className="text-sm font-bold text-slate-900 hover:text-indigo-600 cursor-pointer flex items-center gap-1.5 transition-colors" onClick={() => setSelectedAstrologer(a)} data-id="element-2496">
                          {a.name}
                          {a.badge === 'TOP' && <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200" data-id="element-2497">
                              <Crown className="w-2.5 h-2.5" data-id="element-2498" /> TOP
                            </span>}
                          {a.badge === 'PRO' && <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full border bg-indigo-100 text-indigo-700 border-indigo-200" data-id="element-2499">
                              <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-2500" /> PRO
                            </span>}
                          {a.badge === 'Featured' && <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200" data-id="element-2501">
                              <Gem className="w-2.5 h-2.5" data-id="element-2502" /> Рек.
                            </span>}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5" data-id="element-2503">
                          <span data-id="element-2504">{a.email}</span>
                          <span className="text-slate-300" data-id="element-2505">•</span>
                          <span data-id="element-2506">Вход: {a.lastLogin}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-2507">
                    <div className="text-sm text-slate-700 font-medium" data-id="element-2508">
                      {a.spec}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1" data-id="element-2509">
                      Рег: {a.regDate}
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-2510">
                    <div className="flex flex-col gap-1.5" data-id="element-2511">
                      <div className="text-xs font-medium text-slate-900 flex items-center gap-1" data-id="element-2512">
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-2513" />
                        {a.rating > 0 ? a.rating : '—'}{' '}
                        <span className="text-slate-400 font-normal" data-id="element-2514">
                          ({a.reviews} отз.)
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-600" data-id="element-2515">
                        <span className="flex items-center gap-1" title="Всего заказов" data-id="element-2516">
                          <ShoppingBag className="w-3 h-3 text-slate-400" data-id="element-2517" />{' '}
                          {a.orders}
                        </span>
                        <span className="flex items-center gap-1" title="Активные консультации" data-id="element-2518">
                          <Video className="w-3 h-3 text-slate-400" data-id="element-2519" />{' '}
                          {a.activeConsultations}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-2520">
                    <div className="text-sm font-bold text-emerald-600" data-id="element-2521">
                      {a.earnings}
                    </div>
                    {a.totalEarned > 500000 && <div className="text-[10px] text-amber-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-2522">
                        <TrendingUp className="w-3 h-3" data-id="element-2523" /> Топ доход
                      </div>}
                  </td>
                  <td className="px-4 py-3" data-id="element-2524">
                    <div className="flex flex-col items-start gap-1.5" data-id="element-2525">
                      <Badge variant={a.status === 'active' ? 'success' : a.status === 'blocked' ? 'error' : 'warning'} size="sm" data-id="element-2526">
                        {a.status === 'active' ? 'Активен' : a.status === 'blocked' ? 'Заблокирован' : 'Ожидает'}
                      </Badge>

                      {/* Risk Indicators */}
                      <div className="flex items-center gap-1.5 mt-1" data-id="element-2527">
                        {a.hasActiveChats && <span title="Есть активные чаты" data-id="element-2528">
                            <MessageCircle className="w-3.5 h-3.5 text-blue-500" data-id="element-2529" />
                          </span>}
                        {a.complaintsCount > 0 && <span title={`${a.complaintsCount} жалоб`} data-id="element-2530">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" data-id="element-2531" />
                          </span>}
                        {a.riskScore === 'high' && <span title="Высокий риск (Увод клиентов)" data-id="element-2532">
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-500" data-id="element-2533" />
                          </span>}
                        {a.status === 'blocked' && <span title="Доступ ограничен" data-id="element-2534">
                            <Ban className="w-3.5 h-3.5 text-slate-400" data-id="element-2535" />
                          </span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right relative" data-id="element-2536">
                    <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" icon={<MoreVertical className="w-4 h-4" data-id="element-2538" />} onClick={() => setActionMenuOpen(actionMenuOpen === a.id ? null : a.id)} data-id="element-2537" />

                    {/* Action Dropdown Menu */}
                    <AnimatePresence data-id="element-2539">
                      {actionMenuOpen === a.id && <>
                          <div className="fixed inset-0 z-10" onClick={() => setActionMenuOpen(null)} data-id="element-2540" />
                          <motion.div initial={{
                      opacity: 0,
                      scale: 0.95,
                      y: -10
                    }} animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      scale: 0.95,
                      y: -10
                    }} transition={{
                      duration: 0.15
                    }} className="absolute right-8 top-10 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-20 text-left" data-id="element-2541">
                            <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                        setSelectedAstrologer(a);
                        setActionMenuOpen(null);
                      }} data-id="element-2542">
                              <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-2543" />{' '}
                              Открыть профиль
                            </button>
                            <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                        setSelectedAstrologer(a);
                        setProfileTab('chats');
                        setActionMenuOpen(null);
                      }} data-id="element-2544">
                              <MessageCircle className="w-3.5 h-3.5 text-slate-400" data-id="element-2545" />{' '}
                              Мониторинг чатов
                            </button>
                            <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                        setSelectedAstrologer(a);
                        setProfileTab('orders');
                        setActionMenuOpen(null);
                      }} data-id="element-2546">
                              <ShoppingBag className="w-3.5 h-3.5 text-slate-400" data-id="element-2547" />{' '}
                              Заказы и услуги
                            </button>
                            <div className="h-px bg-slate-100 my-1" data-id="element-2548"></div>
                            <button className="w-full px-4 py-2 text-xs text-amber-600 hover:bg-amber-50 flex items-center gap-2" onClick={() => {
                        success('Ограничения применены');
                        setActionMenuOpen(null);
                      }} data-id="element-2549">
                              <AlertOctagon className="w-3.5 h-3.5" data-id="element-2550" />{' '}
                              Ограничить доступ
                            </button>
                            <button className="w-full px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => {
                        success('Астролог заблокирован');
                        setActionMenuOpen(null);
                      }} data-id="element-2551">
                              <Ban className="w-3.5 h-3.5" data-id="element-2552" /> Заблокировать
                            </button>
                          </motion.div>
                        </>}
                    </AnimatePresence>
                  </td>
                </tr>)}
              {currentAstrologers.length === 0 && <tr data-id="element-2553">
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-id="element-2554">
                    <div className="flex flex-col items-center justify-center" data-id="element-2555">
                      <Search className="w-8 h-8 text-slate-300 mb-3" data-id="element-2556" />
                      <p className="text-sm font-medium" data-id="element-2557">
                        Астрологи не найдены
                      </p>
                      <p className="text-xs mt-1" data-id="element-2558">
                        Попробуйте изменить параметры фильтрации
                      </p>
                    </div>
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center bg-slate-50/50" data-id="element-2559">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-2560" />
          </div>}
      </Card>
    </div>;
}